/**
 * StoryWizard - Multi-step wizard for story generation (Steps 1-3)
 * Step 1: Language, Age, Theme selection + custom ideas
 * Step 2: Skill suggestions + Theme variant selection (mandatory lock-in)
 * Step 3: Main objective selection (mandatory lock-in)
 */

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Card } from "@/components/ui/card.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Progress } from "@/components/ui/progress.tsx";
import { SkillEditModal } from "@/components/story/SkillEditModal.tsx";
import { TextInputModal } from "@/components/story/TextInputModal.tsx";
import { ArrowLeft, ArrowRight, Check, Edit2, Loader2, Sparkles, X } from "lucide-react";
import type { Encounter, GeneratorSettings, Objective, Skill } from "@/types/story.ts";
import type { StorySeedDrafts } from "@/types/story.ts";
import { AVAILABLE_THEMES, getThemeMeta } from "@/lib/data/themes.ts";
import { useEffect, useState } from "react";

const mergeUnique = <T,>(items: T[], getKey: (item: T) => string) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getKey(item).trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const MAX_SELECTED_SKILLS = 4;

interface StoryWizardProps {
  settings: GeneratorSettings;
  drafts: StorySeedDrafts;
  generatingFields: Partial<Record<string, boolean>>;
  generationError: string | null;
  onUpdateSettings: (settings: Partial<GeneratorSettings>) => void;
  onRemoveSuggestion: (
    field: keyof StorySeedDrafts,
    item: Skill | Objective | Encounter | string,
  ) => void;
  onClearDrafts: (fields: Array<keyof StorySeedDrafts>) => void;
  onGenerateStep2: (options?: {
    mode?: "replace" | "append";
    keepSkills?: Skill[];
    keepThemeVariants?: string[];
    forceFresh?: boolean;
  }) => void;
  onGenerateStep3: (options?: {
    mode?: "replace" | "append";
    keepObjectives?: Objective[];
    forceFresh?: boolean;
  }) => void;
  onPreloadStep2: () => Promise<void>;
  onPreloadStep3: (skills: Skill[], themeVariant: string) => Promise<void>;
  onPreloadStep4Bulk: (objective: Objective) => Promise<void>;
  onLockInStep2: (skills: Skill[], themeVariant: string) => void;
  onLockInStep3: (objective: Objective) => void;
  onBack: () => void;
}

export function StoryWizard({
  settings,
  drafts,
  generatingFields,
  generationError,
  onUpdateSettings,
  onRemoveSuggestion,
  onClearDrafts,
  onGenerateStep2,
  onGenerateStep3,
  onPreloadStep2,
  onPreloadStep3,
  onPreloadStep4Bulk,
  onLockInStep2,
  onLockInStep3,
  onBack,
}: StoryWizardProps) {
  const { t } = useTranslation();
  const step = settings.generationStep;
  const isGeneratingStep2 = Boolean(generatingFields.step2);
  const isGeneratingStep3 = Boolean(generatingFields.step3);

  // Step 1 state
  const isCustomTheme = Boolean(settings.theme && !AVAILABLE_THEMES.includes(settings.theme));
  const [customTheme, setCustomTheme] = useState(isCustomTheme ? settings.theme : "");
  const [themeSelection, setThemeSelection] = useState<string>(
    settings.theme ? (isCustomTheme ? "__custom__" : settings.theme) : "__none__",
  );
  const [isCustomThemeSelected, setIsCustomThemeSelected] = useState(
    settings.theme ? isCustomTheme : false,
  );

  // Step 2 state
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>(settings.lockedSkills);
  const [selectedThemeVariant, setSelectedThemeVariant] = useState<string>(settings.themeVariant);

  // Step 3 state
  const [selectedObjective, setSelectedObjective] = useState<Objective | null>(
    settings.generationStep >= 4 ? { description: "", skillUses: [] } : null,
  );
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [editingThemeVariant, setEditingThemeVariant] = useState<string | null>(null);
  const [editingObjective, setEditingObjective] = useState<Objective | null>(null);
  const [showStep2Skeletons, setShowStep2Skeletons] = useState(false);
  const [showStep3Skeletons, setShowStep3Skeletons] = useState(false);

  const skillOptions = mergeUnique([
    ...selectedSkills,
    ...drafts.skills.options.filter(
      (skill) => !selectedSkills.some((selected) => selected.name === skill.name),
    ),
  ], (skill) => skill.name);
  const themeVariantOptions = mergeUnique([
    "",
    ...(selectedThemeVariant ? [selectedThemeVariant] : []),
    ...drafts.themeVariant.options.filter((variant) => variant !== selectedThemeVariant),
  ], (variant) => variant);
  const objectiveOptions = mergeUnique([
    ...(selectedObjective ? [selectedObjective] : []),
    ...drafts.mainObjective.options.filter(
      (objective) => objective.description !== selectedObjective?.description,
    ),
  ], (objective) => objective.description);

  useEffect(() => {
    if (settings.theme) {
      const isCustom = !AVAILABLE_THEMES.includes(settings.theme);
      setThemeSelection(isCustom ? "__custom__" : settings.theme);
      setIsCustomThemeSelected(isCustom);
      if (isCustom) {
        setCustomTheme(settings.theme);
      }
      return;
    }
    if (!isCustomThemeSelected) {
      setThemeSelection("__none__");
    }
  }, [settings.theme, isCustomThemeSelected]);

  useEffect(() => {
    if (!isGeneratingStep2) {
      setShowStep2Skeletons(false);
    }
  }, [isGeneratingStep2]);

  useEffect(() => {
    if (!isGeneratingStep3) {
      setShowStep3Skeletons(false);
    }
  }, [isGeneratingStep3]);

  useEffect(() => {
    if (step !== 1) return;
    if (
      !(settings.language && settings.ageGroup && (settings.theme || themeSelection === "__none__"))
    ) {
      return;
    }

    const timer = setTimeout(() => {
      void onPreloadStep2();
    }, 400);

    return () => clearTimeout(timer);
  }, [
    onPreloadStep2,
    settings.additionalInstructions,
    settings.ageGroup,
    settings.language,
    settings.theme,
    step,
    themeSelection,
  ]);

  useEffect(() => {
    if (step !== 2) return;
    if (selectedSkills.length < MAX_SELECTED_SKILLS && !selectedThemeVariant.trim()) return;

    const timer = setTimeout(() => {
      void onPreloadStep3(selectedSkills, selectedThemeVariant);
    }, 400);

    return () => clearTimeout(timer);
  }, [onPreloadStep3, selectedSkills, selectedThemeVariant, step]);

  useEffect(() => {
    if (step !== 3 || !selectedObjective?.description.trim()) return;

    const timer = setTimeout(() => {
      void onPreloadStep4Bulk(selectedObjective);
    }, 400);

    return () => clearTimeout(timer);
  }, [onPreloadStep4Bulk, selectedObjective, step]);

  const canProceedStep1 = settings.language && settings.ageGroup &&
    (settings.theme || themeSelection === "__none__");

  const canProceedStep2 = selectedSkills.length > 0;

  const canProceedStep3 = selectedObjective !== null;

  const shouldShowStep2SkillsSkeletons = isGeneratingStep2 || showStep2Skeletons ||
    (drafts.skills.options.length === 0 && selectedSkills.length === 0);

  const shouldShowStep2ThemeSkeletons = isGeneratingStep2 || showStep2Skeletons ||
    (drafts.themeVariant.options.length === 0 && !selectedThemeVariant);

  const shouldShowStep3ObjectiveSkeletons = isGeneratingStep3 || showStep3Skeletons ||
    (drafts.mainObjective.options.length === 0 && !selectedObjective);

  const handleStep1Next = () => {
    if (themeSelection === "__custom__" && customTheme) {
      onUpdateSettings({ theme: customTheme });
    }
    onUpdateSettings({ generationStep: 2 });
    // Trigger step 2 generation automatically
    setTimeout(() => onGenerateStep2(), 100);
  };

  const handleStep2AddMore = () => {
    setShowStep2Skeletons(true);
    onGenerateStep2({
      mode: "append",
      keepSkills: [...selectedSkills, ...drafts.skills.options],
      keepThemeVariants: mergeUnique([
        ...drafts.themeVariant.options,
        ...(selectedThemeVariant ? [selectedThemeVariant] : []),
      ], (variant) => variant),
      forceFresh: true,
    });
  };

  const handleStep2LockIn = () => {
    if (!canProceedStep2) return;
    onLockInStep2(selectedSkills, selectedThemeVariant);
    // Trigger step 3 generation automatically
    setTimeout(() => onGenerateStep3(), 100);
  };

  const handleStep3LockIn = () => {
    if (!canProceedStep3 || !selectedObjective) return;
    onLockInStep3(selectedObjective);
  };

  const handleStep3Regenerate = () => {
    setShowStep3Skeletons(true);
    onClearDrafts(["mainObjective"]);
    onGenerateStep3({
      mode: "replace",
      keepObjectives: selectedObjective ? [selectedObjective] : [],
      forceFresh: true,
    });
  };

  const handleStep3AddMore = () => {
    setShowStep3Skeletons(true);
    onGenerateStep3({
      mode: "append",
      keepObjectives: objectiveOptions,
      forceFresh: true,
    });
  };

  const handleSkillToggle = (skill: Skill) => {
    setSelectedSkills((prev) => {
      const exists = prev.find((s) => s.name === skill.name);
      if (exists) {
        return prev.filter((s) => s.name !== skill.name);
      }

      if (prev.length >= MAX_SELECTED_SKILLS) {
        return prev;
      }

      return [...prev, skill];
    });
  };

  const isSkillSelected = (skill: Skill) => {
    return selectedSkills.some((s) => s.name === skill.name);
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-3xl p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={onBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common.back", { defaultValue: "Back to Stories" })}
          </Button>
          <h1 className="text-3xl font-bold mb-2">
            {t("wizard.title", { defaultValue: "Create Your Story" })}
          </h1>
          <p className="text-muted-foreground">
            {step === 1 && t("wizard.step1Subtitle", { defaultValue: "Let's set up your story" })}
            {step === 2 &&
              t("wizard.step2Subtitle", { defaultValue: "Choose skills and theme variant" })}
            {step === 3 && t("wizard.step3Subtitle", { defaultValue: "Pick your main objective" })}
          </p>
        </div>

        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                    s < step
                      ? "bg-primary text-primary-foreground"
                      : s === step
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {s < step ? <Check className="h-5 w-5" /> : s}
                </div>
                {s < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${s < step ? "bg-primary" : "bg-muted"}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Basic Settings */}
        {step === 1 && (
          <div className="space-y-6">
            <Card className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  {t("editor.language", { defaultValue: "Language" })}
                </Label>
                <Select
                  value={settings.language}
                  onValueChange={(value: string) => onUpdateSettings({ language: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="English">English</SelectItem>
                    <SelectItem value="Czech">Čeština</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  {t("editor.ageGroup", { defaultValue: "Age Group" })}: {settings.ageGroup}
                </Label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min={3}
                    max={12}
                    step={1}
                    value={settings.ageGroup === "12+"
                      ? 12
                      : Number.parseInt(settings.ageGroup || "6", 10) || 6}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const next = value >= 12 ? "12+" : String(value);
                      onUpdateSettings({ ageGroup: next });
                    }}
                    aria-label={t("editor.ageGroup", { defaultValue: "Age Group" })}
                    aria-valuetext={settings.ageGroup}
                    className="w-full accent-primary"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  {t("editor.theme", { defaultValue: "Theme" })}
                </Label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* None card */}
                  <Card
                    className={`p-4 cursor-pointer transition-all ${
                      themeSelection === "__none__"
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/50"
                    }`}
                    onClick={() => {
                      setThemeSelection("__none__");
                      setIsCustomThemeSelected(false);
                      setCustomTheme("");
                      onUpdateSettings({ theme: "" });
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xl">🧩</span>
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {t("editor.themeNone", { defaultValue: "None" })}
                        </div>
                        <div className="text-xs text-muted-foreground">No specific theme</div>
                      </div>
                      {themeSelection === "__none__" && <Check className="h-5 w-5 text-primary" />}
                    </div>
                  </Card>

                  {/* Predefined theme cards */}
                  {AVAILABLE_THEMES.map((theme) => {
                    const meta = getThemeMeta(theme);
                    return (
                      <Card
                        key={theme}
                        className={`p-4 cursor-pointer transition-all ${
                          themeSelection === theme
                            ? "border-primary bg-primary/5"
                            : "hover:bg-muted/50"
                        }`}
                        onClick={() => {
                          setThemeSelection(theme);
                          setIsCustomThemeSelected(false);
                          setCustomTheme("");
                          onUpdateSettings({ theme });
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-xl">{meta.emoji}</span>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{theme}</div>
                            <div className="text-xs text-muted-foreground">
                              {meta.description}
                            </div>
                          </div>
                          {themeSelection === theme && <Check className="h-5 w-5 text-primary" />}
                        </div>
                      </Card>
                    );
                  })}

                  {/* Custom card with input inside */}
                  <Card
                    className={`p-4 cursor-pointer transition-all ${
                      themeSelection === "__custom__"
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/50"
                    }`}
                    onClick={() => {
                      setThemeSelection("__custom__");
                      setIsCustomThemeSelected(true);
                      onUpdateSettings({ theme: customTheme });
                    }}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start gap-3">
                        <span className="text-xl">🖋️</span>
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {t("editor.themeCustom", { defaultValue: "Custom" })}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Your own theme idea
                          </div>
                        </div>
                        {themeSelection === "__custom__" && (
                          <Check className="h-5 w-5 text-primary flex-shrink-0" />
                        )}
                      </div>
                      {themeSelection === "__custom__" && (
                        <Input
                          value={customTheme}
                          onChange={(e) => {
                            const nextTheme = e.target.value;
                            setCustomTheme(nextTheme);
                            onUpdateSettings({ theme: nextTheme });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          placeholder={t("editor.customThemePlaceholder", {
                            defaultValue: "e.g., Underwater Adventure...",
                          })}
                          className="text-sm"
                        />
                      )}
                    </div>
                  </Card>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  {t("editor.customInstructions", { defaultValue: "Custom Instructions" })}
                </Label>
                <Textarea
                  value={settings.additionalInstructions}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onUpdateSettings({ additionalInstructions: e.target.value })}
                  placeholder={t("editor.customInstructionsPlaceholder", {
                    defaultValue: "Add any specific requirements...",
                  })}
                  rows={4}
                  className="text-sm"
                />
              </div>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={handleStep1Next}
                disabled={!canProceedStep1}
                size="lg"
                className="w-full sm:w-auto"
              >
                {t("wizard.next", { defaultValue: "Next" })}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Skills + Theme Variant */}
        {step === 2 && (
          <div className="space-y-6">
            {generationError && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-200">
                {generationError}
              </div>
            )}

            <Card className="p-6 space-y-4">
              <div className="space-y-3 text-center">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  {t("editor.mainCharacterName", { defaultValue: "Hero Name" })}
                </Label>
                <Input
                  value={settings.mainCharacterName}
                  onChange={(e) => onUpdateSettings({ mainCharacterName: e.target.value })}
                  placeholder={t("editor.mainCharacterPlaceholder", {
                    defaultValue: "My hero name",
                  })}
                  className="mx-auto h-12 max-w-xl text-center text-lg"
                />
              </div>

              <div className="flex w-full flex-wrap items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={handleStep2AddMore}
                  disabled={isGeneratingStep2}
                  className="w-full sm:w-auto"
                >
                  {isGeneratingStep2
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <Sparkles className="h-4 w-4 mr-2" />}
                  {t("wizard.addMore", { defaultValue: "Add More" })}
                </Button>
              </div>

              {skillOptions.length === 0 && shouldShowStep2SkillsSkeletons
                ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {Array.from({ length: 16 }).map((_, i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                )
                : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {skillOptions.map((skill) => {
                      const selected = isSkillSelected(skill);
                      const canRemove = !selected &&
                        drafts.skills.options.some((opt) => opt.name === skill.name);
                      return (
                        <Card
                          key={skill.name}
                          className={`p-4 cursor-pointer transition-all ${
                            selected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                          }`}
                          onClick={() => handleSkillToggle(skill)}
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">{skill.emoji}</span>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm">{skill.name}</div>
                              {skill.application && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {skill.application}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {selected && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setEditingSkill(skill);
                                  }}
                                  aria-label={t("common.edit", { defaultValue: "Edit" })}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              )}
                              {canRemove && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onRemoveSuggestion("skills", skill);
                                  }}
                                  aria-label={t("common.delete", { defaultValue: "Delete" })}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              {selected && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                    {showStep2Skeletons &&
                      Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={`skill-skel-${i}`} className="h-20 w-full" />
                      ))}
                  </div>
                )}

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{t("wizard.selectedSkills", { defaultValue: "Selected skills" })}</span>
                  <span>{selectedSkills.length}/{MAX_SELECTED_SKILLS}</span>
                </div>
                <Progress value={(selectedSkills.length / MAX_SELECTED_SKILLS) * 100} />
              </div>
            </Card>

            <Card className="p-6 space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-xl font-bold">
                  {t("wizard.selectThemeVariant", { defaultValue: "Select Theme Variant" })}
                </h2>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                  <Button
                    variant="outline"
                    onClick={handleStep2AddMore}
                    disabled={isGeneratingStep2}
                    className="flex-1 sm:flex-none"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t("wizard.addMore", { defaultValue: "Add More" })}
                  </Button>
                </div>
              </div>

              {themeVariantOptions.length === 0 && shouldShowStep2ThemeSkeletons
                ? (
                  <div className="space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                )
                : (
                  <div className="space-y-2">
                    {themeVariantOptions.map((variant) => {
                      const selected = selectedThemeVariant === variant ||
                        (!selectedThemeVariant && variant === "");
                      const canRemove = !selected && drafts.themeVariant.options.includes(variant);
                      return (
                        <Card
                          key={variant || "__none__"}
                          className={`p-4 cursor-pointer transition-all ${
                            selected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                          }`}
                          onClick={() => setSelectedThemeVariant(variant)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 text-sm">
                              {variant || t("common.none", { defaultValue: "None" })}
                            </div>
                            <div className="flex items-center gap-2">
                              {selected && variant && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setEditingThemeVariant(variant);
                                  }}
                                  aria-label={t("common.edit", { defaultValue: "Edit" })}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              )}
                              {canRemove && variant && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onRemoveSuggestion("themeVariant", variant);
                                  }}
                                  aria-label={t("common.delete", { defaultValue: "Delete" })}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              {selected && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                    {showStep2Skeletons &&
                      Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={`variant-skel-${i}`} className="h-16 w-full" />
                      ))}
                  </div>
                )}
            </Card>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <Button
                variant="outline"
                onClick={() => onUpdateSettings({ generationStep: 1 })}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("wizard.back", { defaultValue: "Back" })}
              </Button>
              <Button
                onClick={handleStep2LockIn}
                disabled={!canProceedStep2}
                size="lg"
                className="w-full sm:w-auto"
              >
                {t("wizard.lockIn", { defaultValue: "Lock In & Continue" })}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Main Objective */}
        {step === 3 && (
          <div className="space-y-6">
            {generationError && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-200">
                {generationError}
              </div>
            )}

            <Card className="p-6 space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-xl font-bold">
                  {t("wizard.selectObjective", { defaultValue: "Select Main Objective" })}
                </h2>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                  <Button
                    variant="outline"
                    onClick={handleStep3AddMore}
                    disabled={isGeneratingStep3}
                    className="flex-1 sm:flex-none"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t("wizard.addMore", { defaultValue: "Add More" })}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleStep3Regenerate}
                    disabled={isGeneratingStep3}
                    className="flex-1 sm:flex-none"
                  >
                    {isGeneratingStep3
                      ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t("editor.generating", { defaultValue: "Generating..." })}
                        </>
                      )
                      : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          {t("wizard.regenerate", { defaultValue: "Regenerate" })}
                        </>
                      )}
                  </Button>
                </div>
              </div>

              {objectiveOptions.length === 0 && shouldShowStep3ObjectiveSkeletons
                ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                )
                : (
                  <div className="space-y-2">
                    {objectiveOptions.map((objective) => {
                      const selected = selectedObjective?.description === objective.description;
                      const canRemove = !selected &&
                        drafts.mainObjective.options.some((opt) =>
                          opt.description === objective.description
                        );
                      return (
                        <Card
                          key={objective.description}
                          className={`p-4 cursor-pointer transition-all ${
                            selected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                          }`}
                          onClick={() => setSelectedObjective(selected ? null : objective)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <div className="text-sm">{objective.description}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              {selected && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setEditingObjective(objective);
                                  }}
                                  aria-label={t("common.edit", { defaultValue: "Edit" })}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              )}
                              {canRemove && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onRemoveSuggestion("mainObjective", objective);
                                  }}
                                  aria-label={t("common.delete", { defaultValue: "Delete" })}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              {selected && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                    {showStep3Skeletons &&
                      Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={`objective-skel-${i}`} className="h-20 w-full" />
                      ))}
                  </div>
                )}
            </Card>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <Button
                variant="outline"
                onClick={() => onUpdateSettings({ generationStep: 2 })}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("wizard.back", { defaultValue: "Back" })}
              </Button>
              <Button
                onClick={handleStep3LockIn}
                disabled={!canProceedStep3}
                size="lg"
                className="w-full sm:w-auto"
              >
                {t("wizard.startCreating", { defaultValue: "Start Creating" })}
                <Check className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        <SkillEditModal
          open={Boolean(editingSkill)}
          onClose={() => setEditingSkill(null)}
          skill={editingSkill || { name: "", emoji: "", application: "" }}
          onSave={(updated) => {
            setSelectedSkills((prev) =>
              prev.map((skill) => (skill.name === editingSkill?.name ? updated : skill))
            );
            setEditingSkill(null);
          }}
        />

        <TextInputModal
          open={Boolean(editingThemeVariant)}
          onOpenChange={(open) => {
            if (!open) setEditingThemeVariant(null);
          }}
          title="Edit Theme Variant"
          description="Adjust the theme variant description"
          value={editingThemeVariant || ""}
          onSave={(value) => {
            setSelectedThemeVariant(value);
            setEditingThemeVariant(null);
          }}
          placeholder="e.g., Crowded port city at night"
        />

        <TextInputModal
          open={Boolean(editingObjective)}
          onOpenChange={(open) => {
            if (!open) setEditingObjective(null);
          }}
          title="Edit Main Objective"
          description="Update the objective description"
          value={editingObjective?.description || ""}
          onSave={(value) => {
            const updated = { description: value, skillUses: [] };
            setSelectedObjective(updated);
            setEditingObjective(null);
          }}
          placeholder="e.g., Find the lost treasure before sunset"
        />
      </div>
    </div>
  );
}
